/* Banner carousel — transform-driven carousel with arrows, dots, seamless loop,
   autoplay and pointer dragging.
   Used by banners-with-categories (bannere mari, bannere mici, coloana verticala
   de produse) si de sectiunea "Ai uitat ceva?" din cos.

   De ce transform si nu scroll nativ:
   versiunea precedenta misca pista prin `scrollLeft` cu `scroll-snap-type:
   mandatory`, iar bucla se inchidea teleportand din clona in originalul ei la
   140ms dupa ce scroll-ul se oprea. Ordinea vizibila era: se opreste pe clona ->
   pauza -> sare. In plus scroll-snap reactiona si el la repozitionare, iar
   cronometrul de teleportare putea cadea peste urmatorul pas de autoplay.
   Aici pista se muta cu `translate3d` si o tranzitie CSS, iar teleportarea se
   face in cadrul in care tranzitia s-a terminat, cu tranzitia oprita si un
   reflow fortat intre cele doua scrieri de stil. Continutul de la pozitia noua
   e identic cu cel de la pozitia veche, deci saritura nu are ce sa arate. */
(function () {
  if (customElements.get('banner-carousel')) return;

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Cat sta autoplay-ul pe pauza dupa ce vizitatorul a dat un swipe sau a apasat
  // o sageata.
  var RESUME_AFTER_INTERACTION = 10000;
  // Trebuie sa fie aceeasi valoare ca `--bc-transition` din CSS. E folosita doar
  // pentru cronometrul de rezerva: `transitionend` nu e garantat sa se declanseze
  // (element ascuns, transformare identica, tab in fundal).
  var TRANSITION_MS = 450;
  // Sub atatia pixeli gestul e o atingere, nu un swipe.
  var DRAG_THRESHOLD = 8;
  // Peste fractiunea asta dintr-un pas, sau peste viteza asta, degetul schimba slide-ul.
  var COMMIT_RATIO = 0.18;
  var COMMIT_VELOCITY = 0.45; // px/ms
  // Cat de mult "trage inapoi" pista cand nu e bucla si ai ajuns la capat.
  var EDGE_RESISTANCE = 0.35;

  class BannerCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.banner-carousel__track');
      if (!this.track) return;
      if (!this.track.querySelector(':scope > .grid-banner__item')) return;

      this.prevBtn = this.querySelector('[data-bc-prev]');
      this.nextBtn = this.querySelector('[data-bc-next]');
      this.dotsEl = this.querySelector('.banner-carousel__dots');

      this.autoplayDelay = parseInt(this.dataset.autoplay || '0', 10) || 0;
      this.wantsLoop = this.dataset.loop !== 'false';
      // Bannerele se misca orizontal; coloana de produse, vertical.
      this.vertical = this.dataset.axis === 'y';

      this.real = [];
      this.slides = [];
      this.offset = 0;
      // Indicele absolut in `slides` (deci inclusiv clonele) al slide-ului din stanga/sus.
      this.index = 0;
      // Indicele real, fara clone — pentru buline si pentru pastrarea pozitiei la rebuild.
      this.activeIndex = 0;
      // Translatia curenta, in px. Negativa cand mergem inainte.
      this.position = 0;
      this.loopActive = false;
      this.paused = false;
      this.inView = true;
      this.animating = false;
      this.dragging = false;

      this.onResize = this.onResize.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onTrackScroll = this.onTrackScroll.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);

      this.track.addEventListener('transitionend', this.onTransitionEnd);
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('wheel', this.onWheel, { passive: false });
      // Un container cu `overflow: hidden` ramane derulabil programatic, iar
      // browserul face exact asta cand focusul ajunge pe un link din zona
      // decupata. Ar muta continutul de sub transformare, fara ca noi sa stim.
      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
      this.track.addEventListener('focusin', this.onFocusIn);
      // Un swipe se termina si cu un `click` pe cardul de dedesubt. Il inghitim.
      this.addEventListener('click', this.onClickCapture, true);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.holdAutoplay();
          this.move(-1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.holdAutoplay();
          this.move(1);
        });
      }

      this.track.addEventListener('keydown', (e) => {
        var forward = this.vertical ? 'ArrowDown' : 'ArrowRight';
        var back = this.vertical ? 'ArrowUp' : 'ArrowLeft';
        if (e.key === forward) {
          e.preventDefault();
          this.holdAutoplay();
          this.move(1);
        } else if (e.key === back) {
          e.preventDefault();
          this.holdAutoplay();
          this.move(-1);
        }
      });

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.resume());

      this.build();

      // Pozele se incarca tarziu si schimba layoutul — recalculam cand se aseaza.
      this.imgLoadHandler = () => this.refresh();
      this.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', this.imgLoadHandler, { once: true });
      });

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.refresh());
        this.ro.observe(this.track);

        // Pista e lipita de coloana, deci inaltimea ei nu se mai schimba si
        // observatorul de mai sus nu se mai declanseaza niciodata. Dar blocul de
        // text de sub poza creste dupa aceea — se incarca fonturile, un titlu
        // trece pe al doilea rand — si fiecare pixel castigat acolo e un pixel
        // cu care cardul iese in afara, de doua ori. Il urmarim separat.
        // Inaltimea lui nu depinde de --bc-media-height, deci nu se poate
        // alimenta singur.
        this.cardTextRo = new ResizeObserver(() => this.fitCards());
        this.observeCardText();
      }

      // Fonturile web schimba inaltimea titlului si a butonului dupa primul paint.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.fitCards());
      }

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.inView = entries[0].isIntersecting;
          },
          { threshold: 0.25 }
        );
        this.io.observe(this);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.detachDragListeners();
      if (this.ro) this.ro.disconnect();
      if (this.cardTextRo) this.cardTextRo.disconnect();
      this.clearFitPasses();
      this.clearLayoutChecks();
      cancelAnimationFrame(this._fitRaf);
      if (this.io) this.io.disconnect();
      this.stopAutoplay();
      clearTimeout(this._resumeTimer);
      clearTimeout(this._settleTimer);
      clearTimeout(this._resizeTimer);
      clearTimeout(this._clickTimer);
      clearTimeout(this._wheelTimer);
    }

    /* ---------- slides ---------- */

    // Bannerele pot fi ascunse per dispozitiv (bc-only-desktop / bc-only-mobile),
    // deci conteaza doar cele care chiar sunt in layout.
    realSlides() {
      var all = Array.prototype.slice.call(
        this.track.querySelectorAll(':scope > .grid-banner__item:not([data-bc-clone])')
      );
      return all.filter((el) => el.getClientRects().length > 0);
    }

    clearClones() {
      Array.prototype.forEach.call(
        this.track.querySelectorAll(':scope > [data-bc-clone]'),
        (el) => el.remove()
      );
    }

    makeClone(el) {
      var clone = el.cloneNode(true);
      clone.setAttribute('data-bc-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      // Clonele nu trebuie sa fie accesibile din tastatura si nici anuntate de doua ori.
      Array.prototype.forEach.call(clone.querySelectorAll('a, button, input, [tabindex]'), (n) =>
        n.setAttribute('tabindex', '-1')
      );
      return clone;
    }

    build() {
      var keepActive = this.activeIndex || 0;

      this.clearClones();

      var real = this.realSlides();
      this.real = real;

      if (!real.length) {
        this.slides = [];
        this.loopActive = false;
        this.index = 0;
        this.position = 0;
        this.render(false);
        this.setAttribute('data-static', 'true');
        this.stopAutoplay();
        return;
      }

      // Masuram cu slide-urile reale, inainte sa existe vreo clona.
      this.slides = real;
      this.invalidate();
      var per = this.perView;
      this.lastPerView = per;

      var canLoop = this.wantsLoop && real.length > per;

      // Sunt necesare doar atatea clone cate acopera ecranul la fiecare capat —
      // clonarea intregului set ar insemna 40 de carduri in plus pentru o coloana
      // de 20 de produse.
      var cloneCount = Math.min(this.cloneCount, real.length);
      this.lastCloneCount = cloneCount;

      if (canLoop) {
        var before = document.createDocumentFragment();
        var after = document.createDocumentFragment();
        for (var i = real.length - cloneCount; i < real.length; i++) {
          before.appendChild(this.makeClone(real[i]));
        }
        for (var j = 0; j < cloneCount; j++) {
          after.appendChild(this.makeClone(real[j]));
        }
        this.track.insertBefore(before, this.track.firstChild);
        this.track.appendChild(after);
      }

      this.loopActive = canLoop;
      this.offset = canLoop ? cloneCount : 0;
      this.slides = Array.prototype.slice
        .call(this.track.querySelectorAll(':scope > .grid-banner__item'))
        .filter((el) => el.getClientRects().length > 0);
      this.invalidate();

      this.setAttribute('data-static', real.length <= per ? 'true' : 'false');
      this.buildDots(real.length);

      this.fitCards();
      this.observeCardText();
      this.scheduleFitPasses();
      // fitCards muta inaltimea pozelor, deci si pozitiile slide-urilor.
      this.invalidate();

      // Fiecare din cele douazeci de poze care termina de incarcat declanseaza un
      // rebuild. Daca aici am reveni mereu la primul slide, caruselul ar sari
      // inapoi la inceput timp de cateva secunde dupa incarcarea paginii.
      var restore = Math.min(keepActive, real.length - 1);
      this.index = this.offset + (canLoop ? restore : Math.min(restore, Math.max(0, real.length - per)));
      this.position = -this.offsetOf(this.index);
      this.render(false);

      this.syncActive();
      this.startAutoplay();
      this.scheduleLayoutChecks();
    }

    // Numarul de clone de la fiecare capat se decide din `perView`, iar `perView`
    // se masoara la build. Daca build-ul prinde layoutul neasezat — pozele inca
    // nedecodate, fonturile inca neincarcate — iese `perView: 1` acolo unde ar
    // trebui sa fie 3, se cloneaza un singur card la fiecare capat, si la trecerea
    // buclei se vad doua coloane goale. In mod normal ResizeObserver-ul prinde
    // schimbarea si reconstruieste, dar asta presupune ca observatorul chiar se
    // declanseaza. Trei verificari ieftine il fac sa nu mai fie o presupunere.
    // Nerepornite de build-urile ulterioare: refresh() reconstruieste doar cand
    // ceva s-a schimbat, deci lantul se opreste singur.
    scheduleLayoutChecks() {
      if (this._layoutChecked) return;
      this._layoutChecked = true;
      this._layoutTimers = [120, 400, 1200].map((ms) => setTimeout(() => this.refresh(), ms));
    }

    clearLayoutChecks() {
      (this._layoutTimers || []).forEach(clearTimeout);
      this._layoutTimers = null;
    }

    refresh() {
      // Pozitiile din cache pot fi vechi — de aia suntem chemati.
      this.invalidate();
      var real = this.realSlides();
      var changed = real.length !== this.real.length;
      if (!changed) {
        changed = this.perView !== this.lastPerView;
      }
      if (!changed && this.loopActive) {
        // Latimea unui slide se poate schimba fara ca perView rotunjit sa se
        // schimbe, iar atunci numarul de clone necesare e altul.
        changed = Math.min(this.cloneCount, real.length) !== this.lastCloneCount;
      }
      if (changed) {
        this.build();
      } else {
        this.fitCards();
        // Latimile s-au putut schimba fara ca numarul de slide-uri sa se schimbe
        // (o poza care termina de decodat, o bara de derulare care apare).
        // Reasezam pista pe indicele curent, fara animatie.
        if (!this.dragging && !this.animating) {
          this.invalidate();
          this.position = -this.offsetOf(this.index);
          this.render(false);
        }
        this.syncActive();
      }
    }

    // Coloana de produse se intinde cat ajung bannerele de langa ea, iar asta
    // variaza cu viewportul. Deci inaltimea pozei se masoara din cardul viu, nu e
    // scrisa in cod: ce ramane dupa titlu, pret si buton e al imaginii.
    // Coloana isi ia inaltimea de la bannerele de langa, iar bannerele ajung la
    // inaltimea finala abia dupa ce li se decodeaza imaginile. Observatorii prind
    // cea mai mare parte, dar nu suficient de sigur cat sa pariezi layoutul pe ei:
    // o trecere care cade intre doi pasi de asezare calculeaza o inaltime corecta
    // pentru momentul ala si gresita un cadru mai tarziu, iar nimic n-o mai
    // trezeste. Cateva reverificari ieftine in prima secunda inchid fereastra.
    // Pornite o singura data, in mod deliberat NErepornite de apelurile ulterioare
    // de build(): fiecare din cele douazeci de poze de produs care termina de
    // incarcat declanseaza un rebuild, iar o versiune anterioara se reprograma de
    // fiecare data, asa ca verificarile erau tot amanate si nu rulau niciodata.
    scheduleFitPasses() {
      if (!this.vertical || this._fitInterval) return;

      var passes = 0;
      this._fitInterval = setInterval(() => {
        this.fitCards();
        if (++passes >= 16) this.clearFitPasses();
      }, 250);
    }

    clearFitPasses() {
      clearInterval(this._fitInterval);
      this._fitInterval = null;
    }

    // (Re)indreapta observatorul de text spre primul card curent. build() schimba
    // slide-urile, deci trebuie refacut dupa fiecare rebuild.
    observeCardText() {
      if (!this.cardTextRo || !this.vertical) return;

      this.cardTextRo.disconnect();

      var slide = this.real && this.real[0];
      var info = slide && slide.querySelector('.product-card__info');
      if (info) this.cardTextRo.observe(info);
    }

    fitCards() {
      if (!this.vertical) return;
      var slide = this.real[0];
      if (!slide) return;
      var media = slide.querySelector('.product-card__image');
      if (!media) return;

      var available = this.track.clientHeight;
      if (!available) return;

      var perView = parseFloat(getComputedStyle(this).getPropertyValue('--bc-per-view')) || 2;
      var gap = parseFloat(getComputedStyle(this.track).rowGap) || 0;
      // offsetHeight, nu getBoundingClientRect: motion-element-ul cardului aplica
      // o transformare de scalare, iar rect-ul o include. Masurand prin
      // transformare, poza parea cu ~3px mai inalta decat e, deci rezultatul se
      // aseza cu 4px mai jos si al doilea card ramanea taiat. Inaltimile de layout
      // sunt cele pe care layoutul chiar le foloseste.
      // Acelasi lucru e acum valabil si pentru transformarea pistei.
      var textHeight = slide.offsetHeight - media.offsetHeight;

      var target = Math.round((available - gap * (perView - 1)) / perView - textHeight);
      // Poza nu are voie sa depaseasca spatiul unui card — fara asta, o sughitare
      // de layout se poate alimenta intr-o coloana care creste la nesfarsit.
      var ceiling = Math.floor(available / perView);
      if (target > ceiling) target = ceiling;
      if (target < 60) target = 60;

      if (Math.abs(target - (this._mediaHeight || 0)) > 2) {
        this._mediaHeight = target;
        this.style.setProperty('--bc-media-height', target + 'px');
        // Cardurile si-au schimbat inaltimea, deci pozitiile masurate sunt vechi.
        this.invalidate();

        // textHeight vine din cardul viu, deci o singura trecere cade scurt cand
        // blocul de text inca se aseza in momentul masurarii. Reverificam in
        // cadrul urmator; cand numarul nu se mai misca, zona moarta de 2px de mai
        // sus opreste lantul, deci nu e o bucla.
        cancelAnimationFrame(this._fitRaf);
        this._fitRaf = requestAnimationFrame(() => this.fitCards());
      }
    }

    /* ---------- geometrie (indiferenta la axa) ---------- */

    // Pozitiile se masoara cu getBoundingClientRect RELATIV LA PISTA, nu cu
    // offsetLeft / offsetTop.
    //
    // offsetLeft si offsetTop sunt rotunjite la numar intreg. Latimile reale ale
    // slide-urilor sunt fractionare (`calc((100% - gap*(n-1)) / n)`), asa ca
    // distanta pana la al n-lea slide aduna erorile de rotunjire: pe caruselul
    // din cos, teleportarea cadea cu 0,89px alaturi, iar pe coloana de produse cu
    // 0,44px. Sub-pixel, dar exact la punctul unde bucla trebuie sa fie invizibila
    // — adica exact unde s-ar vedea.
    //
    // Diferenta a doua dreptunghiuri masurate in acelasi sistem anuleaza
    // transformarea pistei (o impartasesc amandoua), iar o transformare pusa pe un
    // DESCENDENT — scalarea `motion-element` a cardurilor — nu intra in
    // dreptunghiul stramosului. Deci masuratoarea e si exacta, si curata.
    //
    // Masuram o singura data per layout si tinem rezultatul in cache: `step` si
    // `perView` sunt citite si in timpul unui drag, de zeci de ori pe secunda,
    // intercalate cu scrieri de transform. Fara cache, ar fi thrashing de layout.
    measure() {
      var key = this.vertical ? 'top' : 'left';
      var base = this.track.getBoundingClientRect()[key];
      var offsets = this.slides.map(function (s) {
        return s.getBoundingClientRect()[key] - base;
      });
      var first = offsets.length ? offsets[0] : 0;
      for (var i = 0; i < offsets.length; i++) offsets[i] -= first;

      this._offsets = offsets;
      this._viewport = this.vertical ? this.track.clientHeight : this.track.clientWidth;
      this._step = offsets.length > 1 && offsets[1] > 0 ? offsets[1] : this._viewport || 1;
      this._perView = Math.max(1, Math.round(this._viewport / this._step));
    }

    // De chemat ori de cate ori layoutul s-a putut schimba: rebuild, resize,
    // poze incarcate, fitCards care muta inaltimea cardurilor.
    invalidate() {
      this._offsets = null;
    }

    ensureMeasured() {
      if (!this._offsets || this._offsets.length !== this.slides.length) this.measure();
    }

    get viewportSize() {
      this.ensureMeasured();
      return this._viewport;
    }

    get step() {
      this.ensureMeasured();
      return this._step;
    }

    get perView() {
      this.ensureMeasured();
      return this._perView;
    }

    get lastIndex() {
      return Math.max(0, this.slides.length - this.perView);
    }

    // Cate slide-uri se cloneaza la fiecare capat.
    //
    // NU `perView`: acela e rotunjit, si e bun pentru paginare, buline si limite.
    // Pentru clone conteaza cate slide-uri ACOPERA viewportul, deci `ceil`. Un
    // viewport care arata 1,18 slide-uri are perView 1, dar are nevoie de 2 clone
    // ca sa nu se vada o fasie goala sub ultima, in timpul tranzitiei de bucla.
    // Plus una de siguranta. Bannerele mici pe mobil sunt exact cazul asta:
    // `--bc-mobile-basis: 78%` arata intentionat o parte din cardul urmator.
    get cloneCount() {
      return Math.max(this.perView, Math.ceil(this.viewportSize / this.step) + 1);
    }

    offsetOf(index) {
      this.ensureMeasured();
      var v = this._offsets[index];
      return v === undefined ? 0 : v;
    }

    /* ---------- randare ---------- */

    applyTransform() {
      var v = Math.round(this.position * 100) / 100;
      this.track.style.transform = this.vertical
        ? 'translate3d(0, ' + v + 'px, 0)'
        : 'translate3d(' + v + 'px, 0, 0)';
    }

    // animate=false inseamna teleportare: punem transformarea cu tranzitia
    // oprita, fortam un reflow ca browserul sa comite pozitia noua in cadrul
    // asta, si abia apoi redam tranzitia inapoi. Fara reflow, cele doua scrieri
    // de stil s-ar aduna intr-un singur cadru si browserul ar anima teleportarea
    // — adica exact taietura pe care o eliminam.
    render(animate) {
      var track = this.track;
      if (animate && !REDUCED_MOTION.matches) {
        track.classList.remove('bc-no-transition');
        this.applyTransform();
        return;
      }
      track.classList.add('bc-no-transition');
      this.applyTransform();
      void track.offsetWidth;
      if (!this.dragging) track.classList.remove('bc-no-transition');
    }

    move(direction) {
      this.goTo(this.index + direction);
    }

    goTo(index, immediate) {
      if (!this.slides.length) return;

      var target = index;
      if (this.loopActive) {
        // Un fling foarte lung poate cere mai multi pasi decat avem clone.
        // Il limitam la ce exista; normalize() aduce oricum pozitia inapoi.
        target = Math.min(Math.max(target, 0), this.slides.length - 1);
      } else {
        target = Math.min(Math.max(target, 0), this.lastIndex);
      }

      var same = target === this.index && Math.abs(this.position + this.offsetOf(target)) < 1;

      this.index = target;
      this.position = -this.offsetOf(target);

      var animate = !immediate && !REDUCED_MOTION.matches && !same;
      this.animating = animate;
      this.render(animate);
      this.syncActive();

      clearTimeout(this._settleTimer);
      if (animate) {
        // transitionend nu e garantat (element ascuns, tab in fundal, transformare
        // identica). Cronometrul de rezerva inchide bucla oricum.
        this._settleTimer = setTimeout(() => this.settle(), TRANSITION_MS + 80);
      } else {
        this.settle();
      }
    }

    settle() {
      clearTimeout(this._settleTimer);
      this.animating = false;
      this.normalize();
    }

    // Iesim din zonele clonate ca vizitatorul sa poata merge la nesfarsit in
    // aceeasi directie. Continutul de la indicele nou e identic cu cel de la
    // indicele vechi, deci saritura n-are ce sa arate — cu conditia sa fie facuta
    // fara tranzitie, in acelasi cadru.
    normalize() {
      if (!this.loopActive || this.dragging) return;
      var n = this.real.length;
      var target = this.index;
      if (target < this.offset) target += n;
      else if (target >= this.offset + n) target -= n;
      if (target === this.index) return;

      this.index = target;
      this.position = -this.offsetOf(target);
      this.render(false);
      this.syncActive();
    }

    /* ---------- swipe ---------- */

    attachDragListeners() {
      window.addEventListener('pointermove', this.onPointerMove, { passive: false });
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    detachDragListeners() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onPointerDown(e) {
      if (this.getAttribute('data-static') === 'true') return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Coloana verticala se trage doar cu mouse-ul. Pe o tableta (768px, unde
      // inca se afiseaza) un drag vertical al nostru ar fura derularea paginii.
      if (this.vertical && e.pointerType !== 'mouse') return;
      // Controalele din card isi pastreaza comportamentul lor.
      if (e.target.closest && e.target.closest('input, select, textarea, [data-bc-prev], [data-bc-next]')) {
        return;
      }

      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragStartTime = performance.now();
      this.dragBase = this.position;
      this.dragIndex = this.index;
      this.dragDelta = 0;
      this.dragDecided = false;
      this.dragging = false;

      // O tranzitie in curs ar lupta cu degetul. O oprim exact acolo unde e.
      if (this.animating) {
        var current = this.currentTranslate();
        this.position = current;
        this.dragBase = current;
        this.animating = false;
        clearTimeout(this._settleTimer);
        this.render(false);
      }

      this.attachDragListeners();
      this.holdAutoplay();
    }

    // Cat s-a translatat pista chiar acum, inclusiv la mijlocul unei tranzitii.
    currentTranslate() {
      var matrix = getComputedStyle(this.track).transform;
      if (!matrix || matrix === 'none') return this.position;
      var parts = matrix.match(/matrix3d\((.+)\)/) || matrix.match(/matrix\((.+)\)/);
      if (!parts) return this.position;
      var values = parts[1].split(',').map(parseFloat);
      if (matrix.indexOf('matrix3d') === 0) return this.vertical ? values[13] : values[12];
      return this.vertical ? values[5] : values[4];
    }

    onPointerMove(e) {
      if (this.dragStartX == null) return;

      var dx = e.clientX - this.dragStartX;
      var dy = e.clientY - this.dragStartY;
      var along = this.vertical ? dy : dx;
      var across = this.vertical ? dx : dy;

      if (!this.dragDecided) {
        if (Math.abs(along) < DRAG_THRESHOLD && Math.abs(across) < DRAG_THRESHOLD) return;
        // Gestul a plecat de-a curmezisul caruselului — e al paginii, nu al nostru.
        if (Math.abs(across) > Math.abs(along)) {
          this.cancelDrag();
          return;
        }
        this.dragDecided = true;
        this.dragging = true;
        this.track.classList.add('bc-no-transition');
        this.classList.add('is-dragging');
      }

      // `touch-action` din CSS opreste deja panoramarea browserului pe axa
      // noastra; asta e pentru mouse si pentru selectia de text.
      if (e.cancelable) e.preventDefault();

      var target = this.dragBase + along;
      if (!this.loopActive) {
        var min = -this.offsetOf(this.lastIndex);
        if (target > 0) target = target * EDGE_RESISTANCE;
        else if (target < min) target = min + (target - min) * EDGE_RESISTANCE;
      }

      this.position = target;
      this.applyTransform();

      this.dragPrevDelta = this.dragDelta;
      this.dragPrevTime = this.dragTime || this.dragStartTime;
      this.dragDelta = along;
      this.dragTime = performance.now();

      this.syncActive();
    }

    onPointerUp() {
      if (this.dragStartX == null) return;

      var wasDragging = this.dragging;
      var along = this.dragDelta || 0;
      // Viteza din ultimul segment de miscare, nu media pe tot gestul: un swipe
      // scurt si rapid la final trebuie sa conteze, chiar daca degetul a stat
      // pe loc inainte.
      var dt = (this.dragTime || 0) - (this.dragPrevTime || 0);
      var velocity = dt > 0 ? (this.dragDelta - (this.dragPrevDelta || 0)) / dt : 0;

      this.finishDrag();
      if (!wasDragging) return;

      var step = this.step;
      var steps = 0;
      if (Math.abs(velocity) > COMMIT_VELOCITY || Math.abs(along) > step * COMMIT_RATIO) {
        steps = Math.max(1, Math.round(Math.abs(along) / step));
        // Tras spre dreapta / in jos (delta pozitiva) inseamna slide-ul anterior.
        if (along > 0) steps = -steps;
      }

      this.goTo(this.dragIndex + steps);
    }

    cancelDrag() {
      this.finishDrag();
      this.position = -this.offsetOf(this.index);
      this.render(false);
    }

    finishDrag() {
      this.detachDragListeners();
      if (this.dragging) {
        // pointerup vine inainte de click, dar pe touch click-ul poate intarzia.
        // Marcajul se stinge la primul click inghitit; cronometrul e doar plasa
        // de siguranta pentru cazul in care nu mai vine niciun click.
        this._suppressClick = true;
        clearTimeout(this._clickTimer);
        this._clickTimer = setTimeout(() => {
          this._suppressClick = false;
        }, 500);
      }
      this.dragging = false;
      this.dragDecided = false;
      this.dragStartX = null;
      this.dragStartY = null;
      this.dragDelta = 0;
      this.dragPrevDelta = 0;
      this.dragTime = 0;
      this.dragPrevTime = 0;
      this.classList.remove('is-dragging');
      this.track.classList.remove('bc-no-transition');
    }

    onClickCapture(e) {
      if (!this._suppressClick) return;
      this._suppressClick = false;
      clearTimeout(this._clickTimer);
      e.preventDefault();
      e.stopPropagation();
    }

    /* ---------- evenimente ---------- */

    onWheel(e) {
      // Doar trackpad-ul orizontal. Rotita verticala ramane a paginii.
      if (this.vertical) return;
      if (this.getAttribute('data-static') === 'true') return;
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      if (Math.abs(e.deltaX) < 8) return;

      e.preventDefault();
      this.holdAutoplay();

      // Un singur gest de trackpad trimite zeci de evenimente. Un pas per gest.
      if (this._wheelLock) return;
      this._wheelLock = true;
      clearTimeout(this._wheelTimer);
      this._wheelTimer = setTimeout(() => {
        this._wheelLock = false;
      }, 400);

      this.move(e.deltaX > 0 ? 1 : -1);
    }

    onTrackScroll() {
      // Vezi comentariul de la inregistrarea listenerului.
      if (this.track.scrollLeft) this.track.scrollLeft = 0;
      if (this.track.scrollTop) this.track.scrollTop = 0;
    }

    onFocusIn(e) {
      this.onTrackScroll();
      var slide = e.target.closest ? e.target.closest('.grid-banner__item') : null;
      if (!slide) return;
      var i = this.slides.indexOf(slide);
      if (i === -1) return;
      // Tab-ul a ajuns pe un card din zona decupata — il aducem in cadru, altfel
      // focusul ar fi pe ceva invizibil.
      if (i < this.index || i >= this.index + this.perView) this.goTo(i);
    }

    onTransitionEnd(e) {
      if (e.target !== this.track) return;
      if (e.propertyName !== 'transform') return;
      this.settle();
    }

    onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.build(), 200);
    }

    onBlockSelect(event) {
      var target = event.target;
      if (!target || !this.contains(target)) return;
      this.pause();
      var index = this.slides.indexOf(target);
      if (index > -1) this.goTo(index);
    }

    /* ---------- buline ---------- */

    buildDots(count) {
      if (!this.dotsEl) return;
      if (this.dotsEl.children.length === count) return;
      this.dotsEl.innerHTML = '';
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'banner-carousel__dot';
        dot.setAttribute('aria-label', 'Banner ' + (i + 1));
        (function (self, index) {
          dot.addEventListener('click', function () {
            self.holdAutoplay();
            self.goTo(self.offset + index);
          });
        })(this, i);
        this.dotsEl.appendChild(dot);
      }
    }

    syncActive() {
      var n = this.real.length;
      if (!n) return;

      // In timpul unui drag indicele oficial nu s-a schimbat inca, dar bulinele
      // trebuie sa urmareasca degetul.
      var current = this.dragging ? Math.round(-this.position / this.step) : this.index;
      var raw = current - this.offset;
      var active = this.loopActive
        ? ((raw % n) + n) % n
        : Math.min(Math.max(raw, 0), Math.max(0, n - this.perView));
      this.activeIndex = active;

      if (this.dotsEl) {
        Array.prototype.forEach.call(this.dotsEl.children, (dot, i) => {
          dot.setAttribute('aria-current', i === active ? 'true' : 'false');
        });
      }

      if (this.loopActive) {
        if (this.prevBtn) this.prevBtn.disabled = false;
        if (this.nextBtn) this.nextBtn.disabled = false;
      } else {
        if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index >= this.lastIndex;
      }
    }

    /* ---------- autoplay ---------- */

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayDelay) return;
      if (REDUCED_MOTION.matches) return;
      if (!this.real.length || this.real.length <= this.perView) return;

      this.autoTimer = setInterval(() => {
        if (this.paused) return;
        if (document.hidden) return;
        if (this.inView === false) return;
        if (this.dragging) return;
        // O tranzitie inca in curs: sarim pasul in loc sa-l taiem la jumatate.
        if (this.animating) return;
        // Prea multe slide-uri pentru o bucla prin clonare (liste lungi de
        // produse): ne intoarcem la inceput in loc sa ramanem blocati la capat.
        if (!this.loopActive) {
          if (this.index >= this.lastIndex) {
            this.goTo(0);
            return;
          }
        }
        this.move(1);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    }

    pause() {
      this.paused = true;
      clearTimeout(this._resumeTimer);
    }

    resume() {
      clearTimeout(this._resumeTimer);
      this.paused = false;
    }

    // Vizitatorul a preluat controlul — ne dam la o parte o vreme, apoi continuam.
    holdAutoplay() {
      this.paused = true;
      clearTimeout(this._resumeTimer);
      this._resumeTimer = setTimeout(() => {
        this.paused = false;
      }, RESUME_AFTER_INTERACTION);
    }
  }

  customElements.define('banner-carousel', BannerCarousel);
})();
